/**
 * SQUARE FOOTAGE UTILITY MODULE
 * Helps users estimate square footage when exact measurements aren't available
 */

/**
 * Get typical square footage ranges by bedroom count and size category
 * Based on Philadelphia market averages
 */
const sqftRanges = {
  0: { // Studio
    small: { min: 350, avg: 450, max: 550 },
    average: { min: 450, avg: 550, max: 650 },
    large: { min: 550, avg: 650, max: 800 }
  },
  1: { // 1BR
    small: { min: 550, avg: 650, max: 750 },
    average: { min: 650, avg: 750, max: 900 },
    large: { min: 750, avg: 900, max: 1100 }
  },
  2: { // 2BR
    small: { min: 750, avg: 850, max: 950 },
    average: { min: 850, avg: 950, max: 1100 },
    large: { min: 950, avg: 1100, max: 1350 }
  },
  3: { // 3BR
    small: { min: 1000, avg: 1100, max: 1250 },
    average: { min: 1100, avg: 1250, max: 1450 },
    large: { min: 1250, avg: 1450, max: 1750 }
  },
  4: { // 4BR
    small: { min: 1300, avg: 1450, max: 1600 },
    average: { min: 1450, avg: 1600, max: 1850 },
    large: { min: 1600, avg: 1850, max: 2200 }
  }
};

/**
 * Get estimated square footage based on bedrooms and size category
 * @param {number} bedrooms - Number of bedrooms (0-4)
 * @param {string} sizeCategory - 'small', 'average', or 'large'
 * @returns {number} Estimated average square footage
 */
function estimateSqft(bedrooms, sizeCategory = 'average') {
  if (!sqftRanges[bedrooms]) {
    throw new Error(`No square footage data for ${bedrooms} bedrooms`);
  }
  
  const validCategories = ['small', 'average', 'large'];
  if (!validCategories.includes(sizeCategory)) {
    throw new Error(`Size category must be one of: ${validCategories.join(', ')}`);
  }
  
  return sqftRanges[bedrooms][sizeCategory].avg;
}

/**
 * Get the full range object for a bedroom/size combination
 * @param {number} bedrooms - Number of bedrooms (0-4)
 * @param {string} sizeCategory - 'small', 'average', or 'large'
 * @returns {Object} Range with min, avg, max
 */
function getSqftRange(bedrooms, sizeCategory = 'average') {
  if (!sqftRanges[bedrooms]) {
    throw new Error(`No square footage data for ${bedrooms} bedrooms`);
  }
  
  return sqftRanges[bedrooms][sizeCategory];
}

/**
 * Determine which size category a property falls into
 * @param {number} bedrooms - Number of bedrooms
 * @param {number} sqft - Actual square footage
 * @returns {string} 'small', 'average', or 'large'
 */
function categorizeSqft(bedrooms, sqft) {
  if (!sqftRanges[bedrooms]) {
    return 'average'; // default if unknown bedroom count
  }
  
  const ranges = sqftRanges[bedrooms];
  
  // Check if it falls in large category
  if (sqft >= ranges.large.min) {
    return 'large';
  }
  
  // Check if it falls in small category
  if (sqft <= ranges.small.max) {
    return 'small';
  }
  
  // Otherwise it's average
  return 'average';
}

/**
 * Get user-friendly description of size category
 * @param {number} bedrooms - Number of bedrooms
 * @param {string} sizeCategory - 'small', 'average', or 'large'
 * @returns {string} Human-readable description
 */
function getSizeDescription(bedrooms, sizeCategory) {
  const range = getSqftRange(bedrooms, sizeCategory);
  const bedroomLabel = bedrooms === 0 ? 'Studio' : `${bedrooms}BR`;
  
  const descriptions = {
    small: `Compact ${bedroomLabel} (${range.min}-${range.max} sqft)`,
    average: `Typical ${bedroomLabel} (${range.min}-${range.max} sqft)`,
    large: `Spacious ${bedroomLabel} (${range.min}-${range.max} sqft)`
  };
  
  return descriptions[sizeCategory] || descriptions.average;
}

/**
 * Get all size options for a given bedroom count
 * Returns array of options for dropdown/select UI
 * @param {number} bedrooms - Number of bedrooms
 * @returns {Array} Array of {value, label, sqft, range} objects
 */
function getSizeOptions(bedrooms) {
  if (!sqftRanges[bedrooms]) {
    return [];
  }
  
  const categories = ['small', 'average', 'large'];
  
  return categories.map(category => {
    const range = sqftRanges[bedrooms][category];
    return {
      value: category,
      label: getSizeDescription(bedrooms, category),
      sqft: range.avg,
      range: `${range.min}-${range.max} sqft`
    };
  });
}

// Export for use in other modules
export {
  estimateSqft,
  getSqftRange,
  categorizeSqft,
  getSizeDescription,
  getSizeOptions,
  sqftRanges
};
